import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Check, Copy, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { enrolTwoFactorSchema, type EnrolTwoFactorInput } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { FloatingField } from '../../../components/FloatingField';
import { useToasts } from '../../../stores/toasts';
import { useBeginTwoFactor, useEnableTwoFactor } from '../api/auth-api';
import { AuthError, AuthLayout, AuthNotice } from '../components/AuthLayout';
import { applyFieldErrors, hasFieldErrors } from './LoginPage';
import styles from './auth-forms.module.css';

/**
 * AUTH-05 — set up two-factor sign-in.
 *
 * Mandatory for HR Lead and Approver, optional for everyone else, and the server
 * enforces the difference — this screen is where a mandatory account is sent, not
 * the thing that makes it mandatory.
 *
 * Three steps, in one screen rather than three: scan, confirm, keep the backup
 * codes. Splitting it across pages would mean somebody closing the tab between the
 * code and the backups, which is the one state with no way out.
 */
export function TwoFactorSetupPage(): ReactNode {
  const navigate = useNavigate();
  const begin = useBeginTwoFactor();
  const enable = useEnableTwoFactor();
  const showToast = useToasts((state) => state.show);
  const [hasCopied, setHasCopied] = useState(false);

  const form = useForm<EnrolTwoFactorInput>({
    resolver: zodResolver(enrolTwoFactorSchema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  });

  // Started once, on arrival: the secret has to exist before there is a QR to show.
  const beginMutate = begin.mutate;
  useEffect(() => {
    beginMutate();
  }, [beginMutate]);

  const onSubmit = form.handleSubmit(async (values) => {
    await enable.mutateAsync(values).catch((error: unknown) => {
      applyFieldErrors(error, form.setError);
    });
  });

  const recoveryCodes = enable.data?.recoveryCodes;

  if (recoveryCodes) {
    return (
      <AuthLayout
        title="Keep these somewhere safe"
        icon={<ShieldCheck size={20} aria-hidden />}
        subtitle="Each code signs you in once if you lose your phone. This is the only time they are shown."
      >
        <div className={styles.codes}>
          {recoveryCodes.map((code) => (
            <span key={code} className={styles.code}>
              {code}
            </span>
          ))}
        </div>

        <Button
          variant="outline"
          size="lg"
          block
          icon={hasCopied ? Check : Copy}
          onClick={() => {
            void navigator.clipboard
              .writeText(recoveryCodes.join('\n'))
              .then(() => {
                setHasCopied(true);
                showToast({ tone: 'success', message: 'Recovery codes copied' });
              })
              .catch(() => {
                showToast({
                  tone: 'danger',
                  message: "Couldn't copy — select the codes and copy them by hand",
                });
              });
          }}
        >
          {hasCopied ? 'Copied' : 'Copy the codes'}
        </Button>

        <Button variant="solid" size="lg" block onClick={() => void navigate({ to: '/' })}>
          Done — take me in
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set up two-factor sign-in"
      subtitle="Scan this with an authenticator app, then type the code it shows."
    >
      {begin.isPending && <AuthNotice tone="info">Preparing your code…</AuthNotice>}

      {begin.data && (
        <>
          <div className={styles.qr}>
            <img src={begin.data.qrDataUrl} alt="QR code for your authenticator app" />
          </div>

          <div className={styles.secret}>
            <span className={styles.secretValue}>{begin.data.secret}</span>
            <Button
              variant="ghost"
              size="sm"
              icon={Copy}
              aria-label="Copy the setup key"
              onClick={() => {
                void navigator.clipboard
                  .writeText(begin.data.secret)
                  .then(() => showToast({ tone: 'success', message: 'Setup key copied' }))
                  .catch(() => undefined);
              }}
            />
          </div>
          <p className={styles.meta}>
            No camera? Type that key into the app by hand instead.
          </p>

          <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
            {enable.error && !hasFieldErrors(enable.error) && (
              <AuthError message={enable.error.message} />
            )}

            <div className={styles.otp}>
              <FloatingField
                {...form.register('code')}
                label="Code from the app"
                hint="Six digits. It changes every 30 seconds."
                error={form.formState.errors.code?.message}
                isRequired
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
              />
            </div>

            <div className={styles.actions}>
              <Button type="submit" variant="solid" size="lg" block isPending={enable.isPending}>
                Turn on two-factor
              </Button>
            </div>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
